import { css } from '@emotion/react';
import { Avatar, Divider, semanticTokens, tokens } from '@flysas-tech/commercial-ui';

import { PriceDetails } from './PriceDetails';
import { TravelerItem } from './types';

const styles = {
  divider: css`
    &:first-of-type {
      display: none;
    }
  `,
  passengerContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-5']};
  `,
  avatar: css`
    display: flex;
    gap: ${tokens.dimension['space-4']};
  `,
  avatarIcon: css`
    flex-shrink: 0;
  `,
  passengerName: css`
    font-size: ${tokens.dimension['space-5']};
    line-height: ${tokens.lineHeight.paragraph['paragraph-1']};
    font-weight: 700;
    display: flex;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    align-items: center;
  `,
  priceContainer: css`
    display: flex;
    align-items: center;
    gap: ${tokens.dimension['space-2']};
  `,
  chevron: css`
    color: ${semanticTokens.color.icon.primary};
  `,
  modalContainer: css`
    width: 100%;
  `,
  modalHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-weight: 700;
    > h3 {
      margin: 0;
    }
  `,

  passengerDetails: css`
    display: flex;
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.secondary};
    flex-wrap: wrap;
    gap: ${tokens.dimension['space-3']};
    overflow: hidden;
  `,
  ancilliaryDetails: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.primary};
  `,

  details: css`
    &::after {
      content: '|';
      color: #ccc;
    }
  `,

  wrapper: css`
    align-content: center;
  `,
};

type Props = {
  pos: string;
  price?: number;
  item: TravelerItem;
  pointsPrice?: React.ReactNode;
};

export const PassengerCard = ({ pos, item, price, pointsPrice }: Props) => {
  const { name, avatar, travelerDetails, ancillaryDetails, ancillaries, fares } = item;

  const flightAndAncillaries = [...fares, ...(ancillaries ?? [])];
  const hasAncillaryDetails = ancillaryDetails && ancillaryDetails.length > 0;
  const hasPriceDetails = price !== undefined && price !== 0;

  return (
    <>
      <div css={styles.divider}>
        <Divider direction={'horizontal'} variant={'subtle'} />
      </div>
      <div css={styles.passengerContainer}>
        <div css={styles.avatar}>
          {avatar && (
            <Avatar
              css={styles.avatarIcon}
              name={avatar.initials ?? ''}
              size="small"
              tier={avatar.tier}
              imgUrl={avatar.imgUrl}
            />
          )}
          <div css={styles.wrapper}>
            <div css={styles.passengerName}>{name}</div>
            <div css={styles.passengerDetails}>{travelerDetails}</div>
          </div>
        </div>

        {hasAncillaryDetails && (
          <div css={styles.ancilliaryDetails}>
            {ancillaryDetails.map((label, index) => {
              const isLast = index === ancillaryDetails.length - 1;
              return (
                <span key={label} css={!isLast ? styles.details : ''}>
                  {label}
                </span>
              );
            })}
          </div>
        )}
        {hasPriceDetails && (
          <PriceDetails priceList={flightAndAncillaries} pos={pos} price={price} pointsPrice={pointsPrice} />
        )}
      </div>
    </>
  );
};
